/**
 * TaskBoard — vue tableau Kanban par statut (glisser-déposer pour changer de statut).
 */
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { CalendarDays, Folder, Repeat } from "lucide-react";
import { Pill } from "../comptabilite/_shared";
import { EXPERTISES, Progress, TASK_PRIORITIES, TASK_STATUSES, dueInfo, meta } from "./taskHelpers";

const DONE_LIMIT = 15;

function Card({ t, onOpen }) {
  const due = dueInfo(t);
  const prio = meta(TASK_PRIORITIES, t.priority);
  return (
    <div onClick={() => onOpen(t)} className={`bg-white border rounded-xl p-3 shadow-sm hover:shadow-md hover:border-emerald-300 transition cursor-pointer ${t.overdue ? "border-rose-200" : "border-slate-200"}`} data-testid={`board-card-${t.id}`}>
      <div className="flex items-start gap-2">
        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${prio.dot}`} title={`Priorité ${prio.label.toLowerCase()}`} />
        <div className={`text-sm font-medium leading-snug flex-1 ${t.status === "terminee" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}{t.recurrence_label && <Repeat className="inline w-3 h-3 ml-1 text-violet-600" title={t.recurrence_label} />}</div>
      </div>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        <Pill cls={meta(EXPERTISES, t.expertise).cls}>{meta(EXPERTISES, t.expertise).label}</Pill>
        {t.master_name && <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 truncate max-w-[160px]"><Folder className="w-3 h-3" />{t.master_name}</span>}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 text-[11px] ${due.cls}`}><CalendarDays className="w-3 h-3" />{due.text}</span>
        <Progress done={t.steps_done} total={t.steps_total} />
      </div>
    </div>
  );
}

export default function TaskBoard({ tasks, onOpen, onStatus }) {
  const onDragEnd = ({ draggableId, destination, source }) => {
    if (!destination || destination.droppableId === source.droppableId) return;
    const t = tasks.find((x) => x.id === draggableId);
    if (t) onStatus(t, destination.droppableId);
  };
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="task-board">
        {TASK_STATUSES.map((s) => {
          const all = tasks.filter((t) => t.status === s.id);
          const items = s.id === "terminee" ? all.slice(0, DONE_LIMIT) : all;
          return (
            <Droppable droppableId={s.id} key={s.id}>
              {(prov, snap) => (
                <div ref={prov.innerRef} {...prov.droppableProps} className={`rounded-xl border-2 flex flex-col min-h-[240px] transition ${snap.isDraggingOver ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-slate-50/60"}`} data-testid={`board-col-${s.id}`}>
                  <div className={`px-3 py-2 border-b rounded-t-xl flex items-center gap-2 ${s.head}`}>
                    <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                    <span className="text-xs font-bold uppercase tracking-wide">{s.label}</span>
                    <span className="ml-auto text-[11px] font-semibold tabular-nums" data-testid={`board-count-${s.id}`}>{all.length}</span>
                  </div>
                  <div className="p-2 space-y-2 flex-1">
                    {items.map((t, idx) => (
                      <Draggable draggableId={t.id} index={idx} key={t.id}>
                        {(p, sn) => (
                          <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps} className={sn.isDragging ? "rotate-1 scale-[1.02]" : ""}>
                            <Card t={t} onOpen={onOpen} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {prov.placeholder}
                    {!all.length && <p className="text-[11px] text-slate-400 text-center py-6">Glissez une tâche ici</p>}
                    {all.length > items.length && <p className="text-[11px] text-slate-400 text-center py-1">+ {all.length - items.length} autres · voir l'onglet Terminées</p>}
                  </div>
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
}
